function _EMOTION_STRINGIFIED_CSS_ERROR__() {
  return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop).";
}
import PropTypes from "prop-types";
import React, { useCallback, useEffect } from 'react';
import { css } from '@emotion/react';
import debounce from 'lodash/debounce';
import { EuiCode, EuiColorPicker, EuiFlexGroup, EuiFlexItem, EuiText, EuiFieldNumber, EuiFieldText, isValidHex, useColorPickerState, useEuiTheme, EuiToolTip, EuiColorPickerSwatch } from '../../../../../src';

// @ts-ignore NOT TS yet
import { humanizeType } from '../../../services/playground/knobs';
import { getDescription } from '../../../services/props/get_description';
export var LANGUAGES = ['javascript', 'html'];
var _ref = process.env.NODE_ENV === "production" ? {
  name: "1qafyux-ThemeValue",
  styles: "padding:0!important;label:ThemeValue;"
} : {
  name: "1qafyux-ThemeValue",
  styles: "padding:0!important;label:ThemeValue;",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__
};
export var ThemeValue = ({
  property,
  name,
  value,
  example,
  groupProps,
  buttonStyle,
  onUpdate,
  type,
  numberProps,
  stringProps,
  colorProps,
  forceUpdateType
}) => {
  const {
    euiTheme
  } = useEuiTheme();
  const [color, setColor, errors] = useColorPickerState(isValidHex(String(value)) ? String(value) : '');
  useEffect(() => {
    if (typeof value === 'string') setColor(value, {
      hex: value,
      isValid: true
    });
    // Safe to omit setState function
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnUpdate = useCallback(debounce((hex, isValid) => {
    if (isValid) {
      onUpdate && onUpdate(hex);
    }
  }, 100), [onUpdate]);
  const handleColorChange = (text, {
    hex,
    isValid
  }) => {
    setColor(text, {
      hex,
      isValid
    });
    debouncedOnUpdate(hex, isValid);
  };
  let exampleRender;
  if (example !== null && (property === 'colors' || name.toLowerCase().includes('color'))) {
    exampleRender = <EuiFlexItem grow={false}>
        <EuiColorPickerSwatch color={color} disabled />
      </EuiFlexItem>;
  } else if (example || buttonStyle) {
    exampleRender = <EuiFlexItem grow={false}>
        <span css={buttonStyle}>{example}</span>
      </EuiFlexItem>;
  }
  let typeRender;
  if (type?.custom?.origin?.type) {
    typeRender = <span css={/*#__PURE__*/css("font-weight:", euiTheme.font.weight.light, ";color:", euiTheme.colors.textSubdued, ";;label:typeRender;")}>
        : {humanizeType(type.custom.origin.type)}
      </span>;
  }
  let descriptionRender;
  if (type?.description) {
    descriptionRender = <>{getDescription(type)}</>;
  }
  const valueRender = () => {
    if (!onUpdate) {
      return <EuiText textAlign="right" size="s" color="subdued">
          <code>{value}</code>
        </EuiText>;
    }
    let updateType = forceUpdateType;
    if (!updateType && (property === 'colors' || name.toLowerCase().includes('color'))) {
      updateType = 'color';
    } else if (!updateType && typeof value === 'number') {
      updateType = 'number';
    } else if (!updateType && property !== 'colors' && !name.toLowerCase().includes('color') && typeof value === 'string') {
      updateType = 'string';
    }
    if (updateType === 'color') {
      return <EuiFlexItem grow={false}>
          <EuiColorPicker swatches={[]} onChange={handleColorChange} color={color} isInvalid={!!errors} compressed {...colorProps} />
        </EuiFlexItem>;
    } else if (updateType === 'number') {
      return <EuiFieldNumber compressed aria-label={`Update ${name} value`} value={Number(value)} onChange={e => onUpdate(Number(e.target.value))} style={{
        width: 64
      }} {...numberProps} />;
    } else if (updateType === 'string') {
      return <EuiFieldText compressed aria-label={`Update ${name} value`} value={String(value)} onChange={e => onUpdate(e.target.value)} style={{
        width: 120
      }} {...stringProps} />;
    }
  };
  name = property ? `${property}.${name}` : name;
  return <EuiFlexGroup responsive={false} alignItems="flexStart" {...groupProps}>
      {exampleRender}
      <EuiFlexItem grow={true}>
        <EuiText size="s">
          <EuiToolTip content={descriptionRender}>
            <EuiCode css={_ref} language="ts" transparentBackground>
              {name}
              {typeRender}
            </EuiCode>
          </EuiToolTip>
        </EuiText>
      </EuiFlexItem>
      <EuiFlexItem grow={false}>{valueRender()}</EuiFlexItem>
    </EuiFlexGroup>;
};
ThemeValue.propTypes = {
  property: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.node,
  example: PropTypes.node,
  groupProps: PropTypes.any,
  buttonStyle: PropTypes.any,
  onUpdate: PropTypes.func,
  type: PropTypes.any,
  numberProps: PropTypes.any,
  stringProps: PropTypes.any,
  colorProps: PropTypes.any,
  forceUpdateType: PropTypes.oneOf(["string", "number", "color"])
};
try {
  ThemeValue.__docgenInfo = {
    tags: {},
    filePath: '/app/packages/eui/src-docs/src/views/theme/customizing/_values.tsx',
    description: '',
    displayName: 'ThemeValue',
    methods: [],
    props: {
      property: {
        defaultValue: null,
        description: '',
        name: 'property',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src-docs/src/views/theme/customizing/_values.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      name: {
        defaultValue: null,
        description: '',
        name: 'name',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src-docs/src/views/theme/customizing/_values.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      value: {
        defaultValue: null,
        description: '',
        name: 'value',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src-docs/src/views/theme/customizing/_values.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      example: {
        defaultValue: null,
        description: '',
        name: 'example',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src-docs/src/views/theme/customizing/_values.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      groupProps: {
        defaultValue: null,
        description: '',
        name: 'groupProps',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src-docs/src/views/theme/customizing/_values.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'EuiFlexGroupProps<"div">'
        }
      },
      buttonStyle: {
        defaultValue: null,
        description: '',
        name: 'buttonStyle',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src-docs/src/views/theme/customizing/_values.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'SerializedStyles'
        }
      },
      onUpdate: {
        defaultValue: null,
        description: '',
        name: 'onUpdate',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src-docs/src/views/theme/customizing/_values.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(color: string | number) => void'
        }
      },
      type: {
        defaultValue: null,
        description: '',
        name: 'type',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src-docs/src/views/theme/customizing/_values.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'any'
        }
      },
      numberProps: {
        defaultValue: null,
        description: '',
        name: 'numberProps',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src-docs/src/views/theme/customizing/_values.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'EuiFieldNumberProps'
        }
      },
      stringProps: {
        defaultValue: null,
        description: '',
        name: 'stringProps',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src-docs/src/views/theme/customizing/_values.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'InputHTMLAttributes<HTMLInputElement> & CommonProps & { icon?: IconType | IconShape; isInvalid?: boolean; ... 7 more ...; compressed?: boolean; }'
        }
      },
      colorProps: {
        defaultValue: null,
        description: '',
        name: 'colorProps',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src-docs/src/views/theme/customizing/_values.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'Partial<EuiColorPickerProps>'
        }
      },
      forceUpdateType: {
        defaultValue: null,
        description: '',
        name: 'forceUpdateType',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src-docs/src/views/theme/customizing/_values.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"string" | "number" | "color"',
          value: [{
            value: '"string"'
          }, {
            value: '"number"'
          }, {
            value: '"color"'
          }]
        }
      }
    },
    extendedInterfaces: []
  };
} catch (e) {}